import React, { useState, useEffect, useRef } from "react";
import api_endpoints from "../../api";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import { Button, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import Divider from "@mui/material/Divider";
import {
  useApiClientQuery,
  useApiClientMutation,
} from "../../hooks/useApiClient";
import { formatTimezoneData } from "../../utils";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";

const Localization = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCountry, setModalCountry] = useState(null); // Store the current country object
  const {
    setShowSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();
  const [selectedLocales, setSelectedLocales] = useState({});
  const [selectedTimezones, setSelectedTimezones] = useState({});
  const [tableFilter, setTableFilter] = useState({
    country_name: "",
    country_code: "",
  });
  const { t } = useCustomTranslation("Localization");

  // track config status (has locales and has timezones) per country id
  const [statusByCountry, setStatusByCountry] = useState({}); // { [countryId]: { hasLocales: bool, hasTimezones: bool } }
  const fetchingStatusRef = useRef(new Set()); // avoid duplicate fetches

  const columns = [
    {
      field: "countryName",
      headerName: t("table_localization_country_name"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "countryCode",
      headerName: t("table_localization_country_code"),
      sortable: true,
      numberSort: false,
    },
  ];

  const filters = {
    countryName: (
      <CustomTextField
        value={tableFilter.country_name || ""}
        onChange={(e) => changeTableFilter("country_name", e.target.value)}
        clearFilter={() => clearTableFilter("country_name")}
      />
    ),
    countryCode: (
      <CustomTextField
        value={tableFilter.country_code || ""}
        onChange={(e) => changeTableFilter("country_code", e.target.value)}
        clearFilter={() => clearTableFilter("country_code")}
      />
    ),
  };

  const allTimezonesQuery = useApiClientQuery(
    () => api_endpoints.getAllTimezones(),
    {
      queryKey: ["getallTimezones"],
      enabled: modalOpen,
    }
  );

  const allLocalesQuery = useApiClientQuery(
    () => api_endpoints.getAllLocales(),
    {
      queryKey: ["getAllLocales"],
      enabled: modalOpen,
    }
  );

  const getCountryTimezonesQuery = useApiClientQuery(
    () => api_endpoints.getCountryTimezones(modalCountry?.id),
    {
      queryKey: [`countryTimezoneQuery_${modalCountry?.id}`],
      enabled: !!modalCountry && modalOpen,
    },
    (data) => {
      setSelectedTimezones((prevSelected) => ({
        ...prevSelected,
        [modalCountry?.id]: formatTimezoneData(data),
      }));
    }
  );

  const getCountryLocalesQuery = useApiClientQuery(
    () => api_endpoints.getCountryLocales(modalCountry?.id),
    {
      queryKey: [
        `countryLocalesQuery_${modalCountry?.id}`,
        modalCountry?.id,
        modalOpen,
      ],
      enabled: !!modalCountry && modalOpen,
    },
    (data) => {
      setSelectedLocales((prevSelected) => ({
        ...prevSelected,
        [modalCountry?.id]: formatLocaleData(data),
      }));
    }
  );

  const getCountriesQuery = useApiClientQuery(
    () =>
      api_endpoints.getCountries(
        tableFilter.country_name,
        tableFilter.country_code
      ),
    {
      queryKey: [`countries_query_${JSON.stringify(tableFilter)}`],
    }
  );

  // fetch configuration status (has locales + timezones) for each country shown
  useEffect(() => {
    const list = getCountriesQuery.data || [];
    let cancelled = false;

    const fetchStatusFor = async (countryId) => {
      try {
        // parallel fetch of locales and timezones for this country
        const [localesRes, tzRes] = await Promise.all([
          api_endpoints.getCountryLocales(countryId),
          api_endpoints.getCountryTimezones(countryId),
        ]);
        if (cancelled) return;

        const hasLocales = Array.isArray(localesRes) || Array.isArray(localesRes?.data)
          ? (Array.isArray(localesRes) ? localesRes.length > 0 : localesRes.data.length > 0)
          : !!(localesRes?.locales?.length);

        const timezonesArray = Array.isArray(tzRes) ? tzRes : tzRes?.data;
        const hasTimezones = Array.isArray(timezonesArray)
          ? timezonesArray.length > 0
          : !!(tzRes?.timezones?.length);

        setStatusByCountry((prev) => ({
          ...prev,
          [countryId]: { hasLocales, hasTimezones },
        }));
      } catch {
        // on error, mark as not configured (or leave undefined)
        setStatusByCountry((prev) => ({
          ...prev,
          [countryId]: prev[countryId] || { hasLocales: false, hasTimezones: false },
        }));
      } finally {
        fetchingStatusRef.current.delete(countryId);
      }
    };

    for (const c of list) {
      const id = c._id || c.id;
      if (!id) continue;
      if (!statusByCountry[id] && !fetchingStatusRef.current.has(id)) {
        fetchingStatusRef.current.add(id);
        fetchStatusFor(id);
      }
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCountriesQuery.data]);

  const changeTableFilter = (option, value) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: value }));
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalCountry(null);
    editCountryLocalesmutation.reset();
    editCountryTimezonesmutation.reset();
  };

  const clearTableFilter = (option) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: "" }));
  };

  const openModal = async (country) => {
    setModalOpen(true);
    setModalCountry(null); // Reset modalCountry state initially
    setModalCountry(country);
  };

  const handleSuccessfulMutation = (successMessage) => {
    setMessageSuccessAlert(successMessage);
    setShowSuccessAlert(true);
    handleCloseModal();
  };

  const editCountryLocalesmutation = useApiClientMutation(
    api_endpoints.editCountryLocales,
    [`edit_countryLocales_${modalCountry?.id}`]
  );

  const editCountryTimezonesmutation = useApiClientMutation(
    api_endpoints.editCountryTimezones,
    [`edit_countryTimezones_${modalCountry?.id}`]
  );

  const handleSubmit = async () => {
    const activeLocales = selectedLocales[modalCountry?.id] || [];
    const localeIds = activeLocales.map((locale) => locale.id);
    const activeTimezone = selectedTimezones[modalCountry?.id] || [];
    const timezoneIds = activeTimezone.map((timezone) => timezone.id);

    const countryLocaleData = {
      countryId: modalCountry?.id,
      locales: localeIds,
    };

    const countryTimezoneData = {
      countryId: modalCountry?.id,
      timezoneIds: timezoneIds,
    };

    try {
      await editCountryLocalesmutation.mutateAsync(countryLocaleData);
      await editCountryTimezonesmutation.mutateAsync(countryTimezoneData);
      handleSuccessfulMutation(t("localization_otp_success_message"));
    } catch (error) {
      console.error("Error updating localization settings:", error);
    }
  };

  const handleChipDelete = (itemToDelete, setSelected) => {
    setSelected((prevSelected) => {
      const updatedChips = { ...prevSelected };
      updatedChips[modalCountry?.id] = updatedChips[modalCountry?.id].filter(
        (item) => item.id !== itemToDelete.id
      );
      return updatedChips;
    });
  };

  const handleLocaleSelect = (event, value) => {
    setSelectedLocales((prevSelected) => ({
      ...prevSelected,
      [modalCountry?.id]: value,
    }));
  };

  const handleTimezoneSelect = (event, value) => {
    setSelectedTimezones((prevSelected) => ({
      ...prevSelected,
      [modalCountry?.id]: value,
    }));
  };

  const formatLocaleData = (data) => {
    // data may arrive as raw array or under .data depending on your hook/wrapper
    const arr = Array.isArray(data) ? data : data?.data || [];
    return arr.map((locale) => {
      if (locale.locale?._id) {
        return {
          id: locale.locale._id,
          label: `${locale.locale.locale} (${locale.locale.locale_abbreviation})`,
          key: locale.locale._id,
        };
      }
      return {
        key: locale._id,
        id: locale._id,
        label: `${locale.locale} (${locale.locale_abbreviation})`,
      };
    });
  };

  // rows from query (apply current search filters server-side via getCountries)
  const allRows =
    getCountriesQuery.data?.map((country) => ({
      id: country._id,
      countryName: country.name,
      countryCode: country.code,
    })) || [];

  // split into configured (has locales AND timezones) vs others
  const configuredRows = [];
  const otherRows = [];
  for (const row of allRows) {
    const status = statusByCountry[row.id];
    const isConfigured = status
      ? status.hasLocales && status.hasTimezones
      : false; // unknown -> treat as not configured until fetched
    (isConfigured ? configuredRows : otherRows).push(row);
  }

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{ "& .MuiDialog-paper": { minHeight: 70, minWidth: 800 } }}
      >
        <DialogTitle>
          {getCountryTimezonesQuery.isFetching || getCountryLocalesQuery.isFetching
            ? t("localization_otp_loading_text")
            : `${t("localization_otp_heading")} ${modalCountry?.countryName || ""}`}{" "}
        </DialogTitle>
        <DialogContent>
          <LoadingContainer
            loading={
              getCountryTimezonesQuery.isFetching ||
              getCountryLocalesQuery.isFetching ||
              editCountryLocalesmutation.isPending ||
              editCountryTimezonesmutation.isPending
            }
            error={
              getCountryTimezonesQuery.isError ||
              getCountryLocalesQuery.isError ||
              editCountryLocalesmutation.isError ||
              editCountryTimezonesmutation.isError
            }
          >
            <div className="flex flex-row space-x-4">
              <div className="flex-1 space-y-4">
                <Autocomplete
                  options={
                    allLocalesQuery.data
                      ? formatLocaleData(allLocalesQuery.data.locales || allLocalesQuery.data)
                      : []
                  }
                  getOptionLabel={(option) => option.label}
                  onChange={handleLocaleSelect}
                  value={selectedLocales[modalCountry?.id] || []}
                  multiple
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  renderTags={() => null}
                  disableClearable
                  renderInput={(params) => (
                    <TextField {...params} label={t("localization_otp_language_select")} />
                  )}
                />

                <div className="flex flex-wrap gap-2">
                  {(selectedLocales[modalCountry?.id] || []).map((locale) => (
                    <Chip
                      key={locale.id}
                      label={locale.label}
                      onDelete={() => handleChipDelete(locale, setSelectedLocales)}
                    />
                  ))}
                </div>
              </div>

              <Divider orientation="vertical" flexItem />

              <div className="flex-1 space-y-4">
                <Autocomplete
                  options={
                    allTimezonesQuery.data
                      ? formatTimezoneData(allTimezonesQuery.data)
                      : []
                  }
                  getOptionLabel={(option) => option.label}
                  onChange={handleTimezoneSelect}
                  value={selectedTimezones[modalCountry?.id] || []}
                  multiple
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  renderTags={() => null}
                  disableClearable
                  renderInput={(params) => (
                    <TextField {...params} label={t("localization_otp_timezone_select")} />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                />
                <div className="flex flex-wrap gap-2">
                  {(selectedTimezones[modalCountry?.id] || []).map((timezone) => (
                    <Chip
                      key={timezone.id}
                      label={timezone.label}
                      onDelete={() => handleChipDelete(timezone, setSelectedTimezones)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </LoadingContainer>
        </DialogContent>
        <DialogActions>
          <ButtonLoading
            loading={
              getCountryTimezonesQuery.isFetching ||
              getCountryLocalesQuery.isFetching ||
              editCountryLocalesmutation.isPending ||
              editCountryTimezonesmutation.isPending
            }
            variant="contained"
            onClick={handleSubmit}
          >
            {t("localization_otp_save_button")}
          </ButtonLoading>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setModalOpen(false)}
          >
            {t("localization_otp_close_button")}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="text-2xl flex justify-between">
        <div className="text-gray-500 text-sm font-normal"></div>
      </div>

      <div className="flex flex-1 h-full mt-5 flex-col space-y-6">
        {/* Filters apply to BOTH lists because server query is already filtered before splitting */}
        <Typography variant="subtitle1" className="font-semibold">
          {t("localization_configured_countries_header") || "Configured Countries (with locales & timezones)"}
        </Typography>
        <DataGrid
          columns={columns}
          rows={configuredRows}
          showDetails={(params) => openModal(params.row)}
          filters={filters}
          loading={getCountriesQuery.isFetching}
        />

        <Divider />

        <Typography variant="subtitle1" className="font-semibold">
          {t("localization_other_countries_header") || "Other Countries"}
        </Typography>
        {/* 
         * Second DataGrid for "Other Countries" - intentionally without filter components.
         * The server query already filters countries into configured/other, so client-side
         * filtering is not needed here. Search functionality still applies to all countries
         * via the single server query above.
         */}
        <DataGrid
          columns={columns}
          rows={otherRows}
          showDetails={(params) => openModal(params.row)}
          // no filters rendered again; search still hits ALL countries via the single server query above
          loading={getCountriesQuery.isFetching}
        />
      </div>
    </>
  );
};

export default Localization;
